import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  CircularProgress,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ParentScreenCtx } from '/src/pages/workbench_build/WorkbenchBuild';
import { treeActions } from '/src/redux/slices/treeSlice';

const AcceleratorWorkflowWizard = ({ open, onClose }) => {
  const { t } = useTranslation();
  const parentScreen = useContext(ParentScreenCtx);
  const dispatch = useDispatch();
  const { rightTree } = useSelector((state) => state.treeData?.[parentScreen] ?? {});
  const acceleratorWorkflowQueue = useSelector((state) => state.treeData?.acceleratorWorkflowQueue);
  
  const [currentStep, setCurrentStep] = useState(0); // 0 = loading, 1+ = data entry
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [formData, setFormData] = useState({});
  const [workflowName, setWorkflowName] = useState('');
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Parse JSON and extract configuration instructions
  const parseWorkflowItems = (translatedData) => {
    const steps = [];
    
    if (!translatedData || !Array.isArray(translatedData)) {
      return steps;
    }

    // Recursively search for workflow items with configuration instructions
    const extractConfigs = (node, workflowName = '') => {
      if (!node) return;


      // Get workflow name if this is a workflow node
      const wfName = node.NAME || workflowName;

      // Check if this node has COMMENTS containing configuration (for any workflow item with ACTION)
      if (node.COMMENTS && node.ACTION) {
        const comments = node.COMMENTS;
        
        const configPattern = /\{\{([^}]+)\}\}:\s*(.+?)(?=\n\{\{|$)/gs;
        const matches = [...comments.matchAll(configPattern)];
        

        if (matches.length > 0) {
          // Create a separate step for each field
          matches.forEach(match => {
            const key = match[1].trim();
            const label = match[2].trim();

            // Push individual step for each field (text field is always empty; no existing value shown)
            steps.push({
              workflowName: wfName,
              workflowItemName: node.NAME || '',
              description: node.DESCRIPTION || '',
              field: {
                key: key,
                label: label,
                value: ''
              },
              nodeReference: node
            });
          });
        }
      }

      // Recursively process children
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach(child => extractConfigs(child, wfName));
      }
    };

    translatedData.forEach(item => extractConfigs(item));
    return steps;
  };

  // Initialize wizard when opened
  useEffect(() => {
    if (open && rightTree?.tree?.body?.translatedData && !rightTree?.loading) {
      const queue = acceleratorWorkflowQueue;
      const currentIndex = queue?.currentIndex ?? 0;
      const expectedFromQueue = (() => {
        const w = queue?.workflows?.[currentIndex];
        const raw = typeof w === 'string' ? w : (w?.name ?? w?.title ?? '');
        return raw?.replace(/\.json$/i, '') ?? '';
      })();
      const currentTemplateName = (rightTree.tree.templateHeader?.WORKFLOW?.NAME || ((rightTree?.path?.split('/')?.pop() ?? '')?.replace(/\.json$/i, '') ?? ''));
      const templateMatchesExpected = !queue || !expectedFromQueue || currentTemplateName.toLowerCase() === expectedFromQueue.toLowerCase();

      if (!templateMatchesExpected) {
        return;
      }

      setCurrentStep(0);
      setFormData({});
      setSavedSuccessfully(false);
      setIsInitialLoading(true);
      
      // Simulate processing delay
      setTimeout(() => {
        const steps = parseWorkflowItems(rightTree.tree.body.translatedData); 
        setWorkflowSteps(steps);
        
        // Initialize form data
        const initialFormData = {};
        steps.forEach((step, stepIndex) => {
          initialFormData[stepIndex] = step.field.value;
        });
        setFormData(initialFormData);

        // Get workflow name from template header
        if (rightTree.tree.templateHeader?.WORKFLOW?.NAME) {
          setWorkflowName(rightTree.tree.templateHeader.WORKFLOW.NAME);
        }

        setIsInitialLoading(false);
        // Move to first step only if steps exist
        if (steps.length > 0) {
          setCurrentStep(1);
        }
      }, 1500); // 1.5 second delay for loading animation
    }
  }, [open, rightTree?.tree?.body?.translatedData, rightTree?.tree?.templateHeader, rightTree?.loading, rightTree?.path, acceleratorWorkflowQueue]);

  // When current workflow has no config steps but queue has next workflow, auto-advance
  useEffect(() => {
    const queue = acceleratorWorkflowQueue;
    const workflows = queue?.workflows;
    const currentIndex = queue?.currentIndex ?? 0;
    const hasNext = Array.isArray(workflows) && currentIndex + 1 < workflows.length;
    const currentTemplateName = rightTree?.tree?.templateHeader?.WORKFLOW?.NAME || (rightTree?.path?.split('/')?.pop() ?? '').replace(/\.json$/i, '');
    const expectedName = (() => {
      const w = workflows?.[currentIndex];
      const raw = typeof w === 'string' ? w : (w?.name ?? w?.title ?? '');
      return raw?.replace(/\.json$/i, '');
    })();
    if (!open || isInitialLoading || rightTree?.loading) return;
    if (workflowSteps.length !== 0) return;
    if (!queue?.workflowGroup || !hasNext) return;

    // Only advance when loaded template matches queue[currentIndex] - prevents skipping when tree hasn't loaded new template yet
    if (currentTemplateName && expectedName && currentTemplateName.toLowerCase() !== expectedName.toLowerCase()) {
      return;
    }

    const nextTemplate = workflows[currentIndex + 1];
    const template = typeof nextTemplate === 'string' ? { name: nextTemplate, title: nextTemplate } : nextTemplate;
    console.log('[AcceleratorWizard] AUTO-ADVANCING to next workflow:', template?.name ?? template, 'index', currentIndex, '->', currentIndex + 1);
    dispatch(treeActions.setAcceleratorWorkflowCurrentIndex(currentIndex + 1));
    dispatch(treeActions.setForceTemplate({
      workflowGroup: queue.workflowGroup,
      template,
      forceSelect: true,
      screen: 'workflow'
    }));
    setCurrentStep(0);
    setWorkflowSteps([]);
    setFormData({});
    setIsInitialLoading(true);
  }, [open, isInitialLoading, workflowSteps.length, acceleratorWorkflowQueue, rightTree?.loading, rightTree?.path, rightTree?.tree?.templateHeader?.WORKFLOW?.NAME, dispatch]);

  const handleInputChange = (stepIndex, value) => {
    setFormData(prev => ({
      ...prev,
      [stepIndex]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < workflowSteps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveChanges = () => {
    
    // Update the JSON template with form data
    const updatedData = JSON.parse(JSON.stringify(rightTree.tree.body.translatedData)); // Deep clone
    
    // Replace only {{VAR}} inside workflow item strings (e.g. "XXX {{CLIENT_ID}}" or "{{CLIENT_ID}} YYY" -> "XXX <value>" or "<value> YYY")
    const replacePlaceholdersInObject = (obj, replacements, excludeKeys = ['COMMENTS']) => {
      if (typeof obj === 'string') {
        let result = obj;
        Object.keys(replacements).forEach(key => {
          const value = replacements[key];
          if (value !== undefined && value !== null && String(value).trim() !== '') {
            const exactPlaceholder = `{{${key}}}`;
            result = result.split(exactPlaceholder).join(value);
          }
        });
        return result;
      } else if (Array.isArray(obj)) {
        // Process each array item
        return obj.map(item => replacePlaceholdersInObject(item, replacements, excludeKeys));
      } else if (typeof obj === 'object' && obj !== null) {
        // Process each object property
        const newObj = {};
        Object.keys(obj).forEach(key => {
          // Skip excluded keys (like COMMENTS)
          if (excludeKeys.includes(key)) {
            newObj[key] = obj[key]; // Keep original value
          } else {
            newObj[key] = replacePlaceholdersInObject(obj[key], replacements, excludeKeys);
          }
        });
        return newObj;
      }
      return obj;
    };
    
    // Keep in COMMENTS only {{key}}: label lines for keys that are not set
    const updateCommentsKeepOnlyUnset = (commentsStr, replacements) => {
      if (!commentsStr || typeof commentsStr !== 'string') return commentsStr;
      const configPattern = /\{\{([^}]+)\}\}:\s*(.+?)(?=\n\{\{|$)/gs;
      const matches = [...commentsStr.matchAll(configPattern)];
      const unsetLines = matches
        .filter(m => {
          const key = m[1].trim();
          const value = replacements[key];
          return value === undefined || value === null || String(value).trim() === '';
        })
        .map(m => m[0].trim());
      return unsetLines.join('\n').trim();
    };
    
    // Group form data by workflow item
    const groupedData = {};
    workflowSteps.forEach((step, stepIndex) => {
      const itemKey = step.workflowItemName;
      if (!groupedData[itemKey]) {
        groupedData[itemKey] = {
          workflowItemName: step.workflowItemName,
          fields: {}
        };
      }
      const fieldValue = formData[stepIndex];
      if (fieldValue !== undefined && fieldValue !== null && fieldValue.trim() !== '') {
        groupedData[itemKey].fields[step.field.key] = fieldValue;
      }
    });
    
    // Update each workflow item with its collected field values
    Object.values(groupedData).forEach((item) => {
      const stepFormData = item.fields;
      
      // Find the corresponding node in updatedData and replace all placeholders
      const updateNodeRecursively = (nodes) => {
        if (!nodes) return false;
        
        for (let i = 0; i < nodes.length; i++) {
          let node = nodes[i];
          
          // Match by NAME and ACTION (any workflow item with an ACTION)
          if (node.NAME === item.workflowItemName && node.ACTION) {
            // Only replace exact placeholder string {{key}} in JSON (no key-based assignment)
            let updatedNode = replacePlaceholdersInObject(node, stepFormData);
            // Update COMMENTS: remove lines for set keys, keep only {{key}}: label for unset keys
            if (updatedNode.COMMENTS) {
              updatedNode = { ...updatedNode, COMMENTS: updateCommentsKeepOnlyUnset(updatedNode.COMMENTS, stepFormData) };
            }
            nodes[i] = updatedNode;
            return true;
          }
          
          if (node.children && updateNodeRecursively(node.children)) {
            return true;
          }
        }
        return false;
      };
      
      updateNodeRecursively(updatedData);
    });

    // Dispatch action to update the tree with modified data
    dispatch(treeActions.setRightTreeTransData({ 
      screen: parentScreen, 
      type: 'body', 
      data: updatedData 
    }));

    // Remove "accelerator" from workflow description only when no configuration is left (all steps filled)
    const allStepsFilled = workflowSteps.length > 0 && workflowSteps.every((_, stepIndex) => {
      const val = formData[stepIndex];
      return val !== undefined && val !== null && String(val).trim() !== '';
    });
    if (allStepsFilled) {
      const templateHeader = rightTree?.tree?.templateHeader;
      const workflow = templateHeader?.WORKFLOW;
      if (workflow && typeof workflow.COMMENTS === 'string') {
        const updatedComments = workflow.COMMENTS.replace(/accelerator/gi, '').replace(/\s+/g, ' ').trim();
        dispatch(treeActions.setRightTreeTemplateHeader({
          screen: parentScreen,
          value: { ...templateHeader, WORKFLOW: { ...workflow, COMMENTS: updatedComments } }
        }));
      }
    }

    dispatch(treeActions.setRightTreeSelectedNode({
      screen: parentScreen,
      type: 'body',
      data: {} // Deselect to force refresh
    }));

    setTimeout(() => {
      dispatch(treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: 'body',
        data: updatedData?.[0] || {} // Reselect to update with new data
      }));
    });

    // If accelerator queue is active, advance to next workflow or finish (when user clicks Save, regardless of whether steps were filled)
    const queue = acceleratorWorkflowQueue;
    const workflows = queue?.workflows;
    const currentIndex = queue?.currentIndex ?? 0;
    const hasNext = Array.isArray(workflows) && currentIndex + 1 < workflows.length;

    if (queue?.workflowGroup) {
      if (hasNext) {
        const nextTemplate = workflows[currentIndex + 1];
        const template = typeof nextTemplate === 'string' ? { name: nextTemplate, title: nextTemplate } : nextTemplate;
        dispatch(treeActions.setAcceleratorWorkflowCurrentIndex(currentIndex + 1));
        dispatch(treeActions.setForceTemplate({
          workflowGroup: queue.workflowGroup,
          template,
          forceSelect: true,
          screen: 'workflow'
        }));
        setSavedSuccessfully(false);
        setCurrentStep(0);
        setWorkflowSteps([]);
        setFormData({});
        setWorkflowName('');
        setIsInitialLoading(true);
        return; // keep wizard open; new template will load
      } else {
        dispatch(treeActions.clearAcceleratorWorkflowQueue());
      }
    }

    setTimeout(() => {
      setSavedSuccessfully(true);
    }, 100);
  };

  const handleClose = () => {
    dispatch(treeActions.clearAcceleratorWorkflowQueue());
    setCurrentStep(0);
    setWorkflowSteps([]);
    setFormData({});
    setWorkflowName('');
    setSavedSuccessfully(false);
    setIsInitialLoading(true);
    onClose();
  };

  const currentStepData = workflowSteps[currentStep - 1];
  const isLoadingStep = currentStep === 0;
  const isLastStep = currentStep === workflowSteps.length;

  // Shared style for Workflow and Item value boxes (same size and color)
  const valueBoxSx = {
    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
    padding: '6px 20px',
    borderRadius: '8px',
    border: '1px solid #90caf9',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.08)',
    minHeight: '36px',
    display: 'flex',
    alignItems: 'center',
  };
  const valueBoxLabelSx = {
    color: 'text.secondary',
    letterSpacing: '0.5px',
    fontWeight: 600,
    fontSize: '1rem',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '550px',
          maxHeight: '85vh',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        pb: 2,
        pt: 3,
        px: 4,
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fafafa'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DescriptionIcon sx={{ color: '#1976d2', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            {t('ACCELERATOR_WF_CONFIG_TIT')}
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="small"
          sx={{ 
            '&:hover': { 
              backgroundColor: 'rgba(0, 0, 0, 0.04)' 
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 4, px: 4, pb: 3 }}>
        {/* Stepper */}
        <Box sx={{ mb: 5, mt: 2 }}>
          <Stepper 
            activeStep={isLoadingStep ? 0 : 1}
            sx={{
              '& .MuiStepLabel-label': {
                fontSize: '0.95rem',
                fontWeight: 500
              },
              '& .MuiStepIcon-root': {
                fontSize: '1.75rem'
              }
            }}
          >
            <Step completed={!isLoadingStep}>
              <StepLabel>{t('ACCELERATOR_STEP_LOAD_WF')}</StepLabel>
            </Step>
            <Step completed={false}>
              <StepLabel>{t('ACCELERATOR_STEP_FILL_DETAILS')}</StepLabel>
            </Step>
          </Stepper>
        </Box>

        {/* Success State - Check this FIRST */}
        {savedSuccessfully ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '350px',
            py: 4
          }}>
            <Box sx={{ 
              backgroundColor: '#e8f5e9', 
              borderRadius: '50%', 
              p: 2.5, 
              mb: 3,
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.15)'
            }}>
              <CheckCircleIcon sx={{ fontSize: 72, color: '#4caf50' }} />
            </Box>
            <Typography variant="h5" sx={{ mt: 1, color: '#2e7d32', fontWeight: 600, mb: 1.5 }}>
              {t('ACCELERATOR_CHANGES_SAVED')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: '400px' }}>
              {t('ACCELERATOR_WF_UPDATED_SAVED')}
            </Typography>
          </Box>
        ) : isInitialLoading ? (
          /* Loading State */
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '350px',
            py: 4
          }}>
            <CircularProgress size={64} thickness={4} sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {t('ACCELERATOR_PROCESSING_WF')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.disabled' }}>
              {t('ACCELERATOR_PLEASE_WAIT_ANALYZE')}
            </Typography>
          </Box>
        ) : workflowSteps.length === 0 ? (
          /* No Configuration Found */
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '350px',
            py: 4
          }}>
            <DescriptionIcon sx={{ fontSize: 64, color: '#bdbdbd', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1 }}>
              {t('ACCELERATOR_NO_CONFIG_REQUIRED')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.disabled', textAlign: 'center', maxWidth: '400px' }}>
              {t('ACCELERATOR_NO_CONFIG_STEPS')}
            </Typography>
          </Box>
        ) : currentStepData ? (
          /* Data Entry Steps */
          <Box sx={{ minHeight: '350px' }}>
            {/* Workflow and Item: label and value on same row; both value boxes same width */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, max-content)',
                alignItems: 'center',
                gap: '8px 16px',
                mb: 4,
                width: 'fit-content',
              }}
            >
              {workflowName && (
                <>
                  <Typography variant="caption" sx={valueBoxLabelSx}>
                    {t('ACCELERATOR_LBL_WORKFLOW')}
                  </Typography>
                  <Box sx={{ ...valueBoxSx, width: '100%', minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#0d47a1', fontSize: '1rem' }} noWrap>
                      {workflowName}
                    </Typography>
                  </Box>
                </>
              )}
              <Typography variant="caption" sx={valueBoxLabelSx}>
                {t('LBL_ITEM')}
              </Typography>
              <Box sx={{ ...valueBoxSx, width: '100%', minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#0d47a1', fontSize: '1rem' }} noWrap>
                  {currentStepData.workflowItemName}
                </Typography>
              </Box>
            </Box>

            {/* Step Progress */}
            <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 600
                  }}
                >
                  {t('ACCELERATOR_STEP_OF').replace('{0}', currentStep).replace('{1}', workflowSteps.length)}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: '#1976d2',
                    fontWeight: 600
                  }}
                >
                  {Math.round(((currentStep - 1) / workflowSteps.length) * 100)}{t('ACCELERATOR_PCT_COMPLETE')}
                </Typography>
              </Box>
              {/* Progress Bar */}
              <Box sx={{ 
                width: '100%', 
                height: '6px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '3px',
                overflow: 'hidden',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <Box sx={{ 
                  width: `${((currentStep - 1) / workflowSteps.length) * 100}%`, 
                  height: '100%', 
                  backgroundColor: '#1976d2',
                  transition: 'width 0.4s ease',
                  borderRadius: '3px',
                  boxShadow: '0 1px 3px rgba(25, 118, 210, 0.3)'
                }} />
              </Box>
            </Box>

            
            

            {/* Input Field */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1.5, 
                  fontWeight: 600, 
                  color: '#424242',
                  fontSize: '0.9rem'
                }}
              >
                {currentStepData.field.label}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                type="text"
                value={formData[currentStep - 1] || ''}
                onChange={(e) => handleInputChange(currentStep - 1, e.target.value)}
                placeholder={t('ACCELERATOR_ENTER_FIELD').replace('{0}', currentStepData.field.label.toLowerCase())}
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#fafafa',
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '14px 16px',
                    fontSize: '0.95rem'
                  }
                }}
              />
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ 
        px: 4, 
        py: 2.5, 
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
        gap: 1.5
      }}>
        {savedSuccessfully ? (
          <Button 
            onClick={handleClose} 
            variant="contained" 
            size="large"
            sx={{ 
              textTransform: 'none',
              px: 4,
              py: 1.25,
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.95rem',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)'
            }}
          >
            {t('BTN_CLOSE')}
          </Button>
        ) : !isInitialLoading && workflowSteps.length === 0 ? (
          <Button 
            onClick={handleClose} 
            variant="outlined"
            size="large"
            sx={{ 
              textTransform: 'none',
              px: 4,
              py: 1.25,
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.95rem'
            }}
          >
            {t('BTN_CLOSE')}
          </Button>
        ) : !isLoadingStep && workflowSteps.length > 0 ? (
          <>
            <Button
              onClick={handlePrevious}
              disabled={currentStep <= 1}
              size="large"
              sx={{ 
                textTransform: 'none',
                px: 3,
                py: 1.25,
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                '&.Mui-disabled': {
                  opacity: 0.5
                }
              }}
            >
              {t('ACCELERATOR_BTN_PREVIOUS')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={handleSaveChanges}
              variant="outlined"
              size="large"
              color="success"
              sx={{ 
                textTransform: 'none',
                px: 3.5,
                py: 1.25,
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px'
                }
              }}
            >
              {t('ACCELERATOR_BTN_SAVE_CHANGES')}
            </Button>
            <Button
              onClick={handleNext}
              disabled={isLastStep}
              variant="contained"
              size="large"
              sx={{ 
                textTransform: 'none',
                px: 3.5,
                py: 1.25,
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '0.95rem',
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)',
                '&.Mui-disabled': {
                  opacity: 0.5
                }
              }}
            >
              {t('ACCELERATOR_BTN_NEXT')}
            </Button>
          </>
        ) : (isLoadingStep || workflowSteps.length === 0) ? (
          <Button 
            onClick={handleClose} 
            size="large"
            sx={{ 
              textTransform: 'none',
              px: 4,
              py: 1.25,
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.95rem'
            }}
          >
            {t('BTN_CLOSE')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default AcceleratorWorkflowWizard;
